import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchProduct, fetchProducts, fetchReviews, postReview } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingBag, MessageCircle, Star, Minus, Plus, Check, ChevronLeft, ChevronRight, MessageSquareHeart, Users } from "lucide-react";
import { waLink, DIWALI_INFO } from "../constants/data";
import { toast } from "sonner";
import ProductCard from "../components/shop/ProductCard";
import ComingSoonPage from "../components/shared/ComingSoonPage";
import RecentlyViewed from "../components/home/RecentlyViewed";
import { addRecentlyViewed } from "../lib/recentlyViewed";

const NOTE_MAX = 200;

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });
  const [activeImg, setActiveImg] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null); // {id, color_name, color_hex, images, stock} or null
  const [noteEnabled, setNoteEnabled] = useState(false);
  const [note, setNote] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    setQty(1);
    setActiveImg(0);
    setSelectedVariant(null);
    setNoteEnabled(false);
    setNote("");
    fetchProduct(slug).then(async (p) => {
      setProduct(p);
      addRecentlyViewed(p);
      // Restore variant from ?color=<variant_id> if present in URL
      const wanted = searchParams.get("color");
      if (wanted && Array.isArray(p.variants)) {
        const match = p.variants.find((v) => v.id === wanted);
        if (match) setSelectedVariant(match);
      }
      const [r, rel] = await Promise.all([
        fetchReviews(p.id),
        fetchProducts({ category: p.category }),
      ]);
      setReviews(r);
      setRelated(rel.filter((x) => x.id !== p.id).slice(0, 4));
    }).catch(() => setProduct(null));
  }, [slug]);

  if (!product) return <div className="pt-32 pb-20 container-x">Loading…</div>;

  const isDiwali = product.category === "diwali" || (product.categories || []).includes("diwali");
  if (isDiwali) {
    return (
      <ComingSoonPage
        pageId="diwali"
        pageTestId="product-diwali-coming-soon"
        titleLine1={product.name}
        titleLine2={DIWALI_INFO.greeting}
        description={<>This Diwali hamper is still being wrapped with love — check back this Diwali!</>}
        subCopy="Drop your email and we'll let you know the moment it's available to order."
        accentColor="#E08036"
      />
    );
  }

  const avgRating = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  // Variant-aware view (falls back to base product for any empty override)
  const variantImgs = (selectedVariant?.images || []).filter(Boolean);
  const displayImages = variantImgs.length > 0 ? variantImgs : product.images;
  const displayStock = selectedVariant ? selectedVariant.stock : product.stock;
  const overrideOr = (key) => {
    const v = selectedVariant && (selectedVariant[key] || "").toString().trim();
    return v ? selectedVariant[key] : product[key];
  };
  const displayPrice = selectedVariant && (selectedVariant.price ?? null) !== null ? selectedVariant.price : product.price;
  const displayShortDesc = overrideOr("short_description");
  const displayDescription = overrideOr("description");
  const displayProductInfo = overrideOr("product_info");
  const displayWhyHamper = overrideOr("why_hamper");
  const displayTags = (selectedVariant && Array.isArray(selectedVariant.tags) && selectedVariant.tags.length > 0)
    ? selectedVariant.tags
    : (product.tags || []);
  const outOfStock = displayStock <= 0;
  const variants = product.variants || [];

  const chooseVariant = (v) => {
    const isDeselect = selectedVariant?.id === v.id;
    setSelectedVariant(isDeselect ? null : v);
    setActiveImg(0);
    setQty(1);
    // Keep URL in sync so shopper can share/back-nav to this colour
    const params = new URLSearchParams(searchParams);
    if (isDeselect) params.delete("color");
    else params.set("color", v.id);
    setSearchParams(params, { replace: true });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.comment) return toast.error("Please complete all fields");
    const r = await postReview({ ...reviewForm, product_id: product.id });
    setReviews([r, ...reviews]);
    setReviewForm({ name: "", rating: 5, comment: "" });
    toast.success("Thank you for your review!");
  };

  return (
    <div className="pt-24" data-testid="product-detail-page">
      <Helmet>
        <title>{`${product.name} — Naathi Charami Gifts & Hampers`}</title>
        <meta name="description" content={(product.short_description || product.description || product.name).slice(0, 158)} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} — Naathi Charami`} />
        <meta property="og:description" content={(product.short_description || product.description || product.name).slice(0, 158)} />
        {product.images?.[0] && <meta property="og:image" content={product.images[0]} />}
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content="INR" />
        <link rel="canonical" href={`https://naathicharamigifts.com/product/${product.slug}`} />
        {/* Product JSON-LD for Google rich result */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Product",
          name: product.name,
          image: (product.images || []).filter(Boolean),
          description: product.description || product.short_description || product.name,
          sku: product.slug,
          brand: { "@type": "Brand", name: "Naathi Charami Gifts & Hampers" },
          category: product.category,
          offers: {
            "@type": "Offer",
            url: `https://naathicharamigifts.com/product/${product.slug}`,
            priceCurrency: "INR",
            price: product.price,
            availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: { "@type": "Organization", name: "Naathi Charami Gifts & Hampers" },
          },
          ...(reviews.length > 0 && {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: Number(avgRating.toFixed(1)),
              reviewCount: reviews.length,
              bestRating: 5,
              worstRating: 1,
            },
            review: reviews.slice(0, 5).map((r) => ({
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
              author: { "@type": "Person", name: r.name },
              reviewBody: r.comment,
              ...(r.created_at && { datePublished: r.created_at }),
            })),
          }),
        })}</script>
        {/* Breadcrumb JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://naathicharamigifts.com/" },
            { "@type": "ListItem", position: 2, name: "Shop", item: `https://naathicharamigifts.com/shop/${product.category}` },
            { "@type": "ListItem", position: 3, name: product.name },
          ],
        })}</script>
      </Helmet>
      <section className="section-y">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <div data-testid="product-gallery">
            <div className="relative aspect-[3/4] overflow-hidden bg-[#FFE7F1] border-2 border-[#2D2424] rounded-3xl" style={{ boxShadow: "5px 5px 0 #2D2424" }}>
              <img
                src={displayImages[activeImg] || displayImages[0]}
                alt={product.name}
                className="w-full h-full object-cover transition-opacity duration-300"
                data-testid="product-main-image"
                key={`${selectedVariant?.id || "base"}-${activeImg}`}
              />
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImg((activeImg - 1 + displayImages.length) % displayImages.length)}
                    data-testid="gallery-prev"
                    aria-label="previous image"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-2 border-[#2D2424] bg-white/95 hover:bg-[#FFE7F1] flex items-center justify-center"
                    style={{ boxShadow: "2px 2px 0 #2D2424" }}
                  >
                    <ChevronLeft className="w-5 h-5"/>
                  </button>
                  <button
                    onClick={() => setActiveImg((activeImg + 1) % displayImages.length)}
                    data-testid="gallery-next"
                    aria-label="next image"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-2 border-[#2D2424] bg-white/95 hover:bg-[#FFE7F1] flex items-center justify-center"
                    style={{ boxShadow: "2px 2px 0 #2D2424" }}
                  >
                    <ChevronRight className="w-5 h-5"/>
                  </button>
                  <div
                    data-testid="gallery-counter"
                    className="absolute bottom-3 right-3 px-3 py-1 rounded-full border-2 border-[#2D2424] bg-white/95 font-doodle text-sm text-[#7A1F2B]"
                    style={{ boxShadow: "2px 2px 0 #2D2424" }}
                  >
                    {activeImg + 1} / {displayImages.length}
                  </div>
                </>
              )}
            </div>
            {displayImages.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-1" data-testid="product-thumbnails" style={{ scrollbarWidth: "none" }}>
                {displayImages.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`product-thumb-${i}`}
                    aria-label={`view image ${i + 1}`}
                    className={`shrink-0 w-16 h-20 sm:w-20 sm:h-[100px] bg-[#FFE7F1] overflow-hidden rounded-2xl border-2 transition-all ${
                      activeImg === i
                        ? "border-[#7A1F2B] scale-[1.02]"
                        : "border-[#2D2424]/15 hover:border-[#2D2424]"
                    }`}
                    style={activeImg === i ? { boxShadow: "3px 3px 0 #7A1F2B" } : {}}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Link to={`/shop/${product.category}`} className="label-small">{product.category === "her" ? "Gifts for Her" : product.category === "him" ? "Gifts for Him" : product.category === "kids" ? "Gifts for Kids" : product.category === "babies" ? "Gifts for Babies" : product.category === "baskets" ? "Baskets Collection" : product.category === "diwali" ? "Diwali Hampers" : product.category}</Link>
            <h1 className="font-doodle text-5xl sm:text-6xl text-[#2D2424] mt-2">{product.name}</h1>
            {displayShortDesc && (
              <p className="mt-3 text-lg font-hand italic text-[#7A1F2B]" data-testid="product-tagline">{displayShortDesc}</p>
            )}

            <div className="flex items-center gap-3 mt-5">
              <div className="flex">
                {[1,2,3,4,5].map((s) => (
                  <Star key={s} className="w-4 h-4" strokeWidth={1.5} fill={s <= Math.round(avgRating) ? "#7A1F2B" : "none"} color="#7A1F2B"/>
                ))}
              </div>
              <span className="text-sm font-hand text-[#5C5555]">{reviews.length} review{reviews.length === 1 ? "" : "s"}</span>
            </div>

            <div className="flex items-baseline gap-3 mt-6">
              <span className="font-doodle text-5xl text-[#7A1F2B]" data-testid="product-price">₹{displayPrice.toLocaleString("en-IN")}</span>
              {product.mrp && product.mrp > displayPrice && (
                <span className="text-lg font-hand text-[#5C5555] line-through">₹{product.mrp.toLocaleString("en-IN")}</span>
              )}
            </div>

            <p className={`mt-4 font-hand text-base ${outOfStock ? "text-[#7A1F2B]" : "text-green-700"}`} data-testid="product-stock">
              {outOfStock ? "❌ Currently out of stock" : `✓ ${displayStock} in stock`}
            </p>

            {/* Color swatches — base + variants (only if colours are configured) */}
            {(() => {
              const baseHasColor = !!(product.color_hex || product.color_name);
              const variantList = Array.isArray(variants) ? variants : [];
              // Build a unified list: base first, then variants
              const allColors = [
                ...(baseHasColor || variantList.length > 0
                  ? [{
                      id: "base",
                      color_name: product.color_name || "Default",
                      color_hex: product.color_hex || "#FFE7F1",
                      stock: product.stock,
                      isBase: true,
                    }]
                  : []),
                ...variantList,
              ];
              // Nothing to show if no base color and no variants
              if (allColors.length === 0) return null;
              const activeId = selectedVariant ? selectedVariant.id : "base";
              return (
                <div className="mt-6" data-testid="variant-selector">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#5C5555]">Colour</span>
                    <span className="text-sm font-hand text-[#7A1F2B]">
                      {selectedVariant ? selectedVariant.color_name : (product.color_name || "Default")}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {allColors.map((v) => {
                      const isSelected = activeId === v.id;
                      const soldOut = (v.stock || 0) <= 0;
                      const handleClick = () => {
                        if (v.isBase) {
                          if (selectedVariant) {
                            // switch back to base
                            setSelectedVariant(null);
                            setActiveImg(0);
                            setQty(1);
                            const params = new URLSearchParams(searchParams);
                            params.delete("color");
                            setSearchParams(params, { replace: true });
                          }
                        } else {
                          chooseVariant(v);
                        }
                      };
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={handleClick}
                          data-testid={`variant-swatch-${v.id}`}
                          title={`${v.color_name}${soldOut ? " — sold out" : ""}`}
                          aria-label={v.color_name}
                          className={`relative w-11 h-11 rounded-full border-2 transition-transform ${isSelected ? "border-[#7A1F2B] scale-110" : "border-[#2D2424]/60 hover:scale-105"}`}
                          style={{
                            backgroundColor: v.color_hex || "#FFE7F1",
                            boxShadow: isSelected ? "3px 3px 0 #7A1F2B" : "2px 2px 0 #2D2424",
                          }}
                        >
                          {isSelected && (
                            <span className="absolute inset-0 rounded-full ring-2 ring-white pointer-events-none" />
                          )}
                          {soldOut && (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="w-11 h-0.5 bg-[#2D2424] rotate-45 rounded-full"/>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="flex items-center gap-3 mt-6">
              <span className="text-sm font-hand text-[#5C5555]">Quantity</span>
              {(() => {
                const isDiwali = product.category === "diwali";
                // Diwali hampers: cap at 5 per order (or product stock, whichever is lower).
                // Bulk enquiries go via the WhatsApp button below.
                const perOrderCap = isDiwali ? Math.min(5, displayStock) : displayStock;
                return (
                  <div className="flex items-center border-2 border-[#2D2424] rounded-full overflow-hidden" style={{ boxShadow: "2px 2px 0 #2D2424" }}>
                    <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="px-3 py-2 hover:bg-[#FFE7F1]"><Minus className="w-4 h-4"/></button>
                    <span className="px-5 font-doodle text-2xl" data-testid="qty-value">{qty}</span>
                    <button
                      onClick={() => setQty(Math.min(perOrderCap, qty + 1))}
                      disabled={qty >= perOrderCap}
                      data-testid="qty-plus"
                      className="px-3 py-2 hover:bg-[#FFE7F1] disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      <Plus className="w-4 h-4"/>
                    </button>
                  </div>
                );
              })()}
              {product.category === "diwali" && (
                <span className="text-[11px] font-hand text-[#5C5555] italic" data-testid="diwali-qty-note">
                  Max 5 per order · Bulk? See below
                </span>
              )}
            </div>

            <button
              onClick={() => {
                const finalNote = noteEnabled ? note.trim() : "";
                addItem({ ...product, price: displayPrice }, qty, finalNote, selectedVariant);
                const suffix = selectedVariant ? ` (${selectedVariant.color_name})` : "";
                toast.success(finalNote ? `Added${suffix} with your note 💌` : `Added ${qty} × ${product.name}${suffix} 🎁`);
              }}
              disabled={outOfStock}
              data-testid="product-add-to-cart"
              className="btn-primary w-full mt-6 disabled:bg-[#5C5555]"
            >
              <ShoppingBag className="w-4 h-4"/> Add to Cart
            </button>

            {/* Bulk order (WhatsApp) — Diwali hampers only. Prefilled with hamper name. */}
            {product.category === "diwali" && (
              <a
                href={waLink(DIWALI_INFO.bulkMessage(product.name))}
                target="_blank"
                rel="noreferrer"
                data-testid="product-bulk-order-whatsapp"
                className="btn-whatsapp w-full mt-3 justify-center"
              >
                <Users className="w-4 h-4"/> Bulk Order (WhatsApp)
              </a>
            )}

            {/* Personalised note */}
            <div className="mt-3" data-testid="personalised-note-block">
              <label className="inline-flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={noteEnabled}
                  onChange={(e) => setNoteEnabled(e.target.checked)}
                  data-testid="personalised-note-toggle"
                  className="mt-1 w-4 h-4 accent-[#7A1F2B] cursor-pointer"
                />
                <span className="font-hand text-sm text-[#2D2424] inline-flex items-center gap-1.5">
                  <MessageSquareHeart className="w-4 h-4 text-[#7A1F2B]"/> Add a personalised note
                  <span className="text-xs text-[#5C5555] font-hand">(free, handwritten on the hamper)</span>
                </span>
              </label>

              {noteEnabled && (
                <div className="mt-2" data-testid="personalised-note-input-wrap">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                    placeholder="Dear Riya, wishing you a year as bright as you are..."
                    rows={3}
                    data-testid="personalised-note-input"
                    className="w-full px-3 py-2 border-2 border-[#2D2424] rounded-2xl bg-[#FFFAF3] font-hand text-sm text-[#2D2424] placeholder-[#9B9494] focus:outline-none focus:border-[#7A1F2B] resize-none"
                    style={{ boxShadow: "3px 3px 0 #2D2424" }}
                  />
                  <p className="mt-1 text-[11px] font-hand text-[#5C5555] flex justify-between">
                    <span>Up to {NOTE_MAX} characters · We&apos;ll hand-letter this on a card.</span>
                    <span>{note.length}/{NOTE_MAX}</span>
                  </p>
                </div>
              )}
            </div>

            <a
              href={waLink(`Hi! I'd like a bulk inquiry for ${product.name}${selectedVariant ? ` in ${selectedVariant.color_name}` : ""}.`)}
              target="_blank" rel="noreferrer"
              data-testid="product-bulk-inquiry"
              className="mt-3 btn-secondary w-full"
            >
              <MessageCircle className="w-4 h-4"/> Bulk / Custom Inquiry
            </a>
          </div>
        </div>
      </section>

      {/* Description / Product Info / Why this Hamper */}
      <section className="border-t border-[#E5DBD8] bg-white">
        <div className="container-x py-12 grid lg:grid-cols-3 gap-8" data-testid="product-info-sections">
          <div className="doodle-card p-6 bg-[#FFE7F1]" data-testid="section-description">
            <h3 className="font-doodle text-3xl text-[#2D2424]">Description</h3>
            <p className="mt-4 text-sm font-hand leading-relaxed text-[#2D2424] whitespace-pre-line">{displayDescription}</p>
          </div>

          <div className="doodle-card p-6 bg-[#E7F4FF]" data-testid="section-product-info">
            <h3 className="font-doodle text-3xl text-[#2D2424]">Product Info</h3>
            {displayProductInfo ? (
              <p className="mt-4 text-sm font-hand leading-relaxed text-[#2D2424] whitespace-pre-line">{displayProductInfo}</p>
            ) : (
              <ul className="mt-4 text-sm font-hand text-[#2D2424] space-y-2">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7A1F2B] mt-0.5 shrink-0" strokeWidth={2}/>Premium curated contents</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7A1F2B] mt-0.5 shrink-0" strokeWidth={2}/>Hand-assembled with care</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7A1F2B] mt-0.5 shrink-0" strokeWidth={2}/>Premium gift box packaging</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7A1F2B] mt-0.5 shrink-0" strokeWidth={2}/>Personalized note included on request</li>
              </ul>
            )}
          </div>

          <div className="doodle-card p-6 bg-[#FFF7DC]" data-testid="section-why-hamper">
            <h3 className="font-doodle text-3xl text-[#2D2424]">Why this Hamper</h3>
            <p className="mt-4 text-sm font-hand leading-relaxed text-[#2D2424] whitespace-pre-line">
              {displayWhyHamper || `${displayShortDesc || product.name} — thoughtfully curated by Naathi Charami. Every detail is hand-picked so you can gift with love, without the hassle. Perfect for every meaningful occasion. ✨`}
            </p>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-y bg-[#F2E8E4]/40 border-y border-[#E5DBD8]">
        <div className="container-x grid lg:grid-cols-2 gap-12">
          <div>
            <p className="label-small">Customer Reviews</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 text-[#2D2424]">What our customers say</h2>
            <div className="mt-6 space-y-5" data-testid="reviews-list">
              {reviews.length === 0 ? (
                <p className="text-[#5C5555] italic">Be the first to review this hamper.</p>
              ) : reviews.map((r) => (
                <div key={r.id} className="bg-white border border-[#E5DBD8] p-5">
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-lg text-[#2D2424]">{r.name}</p>
                    <div className="flex">
                      {[1,2,3,4,5].map((s) => (
                        <Star key={s} className="w-3.5 h-3.5" strokeWidth={1.5} fill={s <= r.rating ? "#7A1F2B" : "none"} color="#7A1F2B"/>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-[#5C5555] mt-2">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={submitReview} className="bg-white border border-[#E5DBD8] p-6 self-start" data-testid="review-form">
            <h3 className="font-serif text-2xl text-[#2D2424]">Write a Review</h3>
            <input
              type="text"
              placeholder="Your name"
              value={reviewForm.name}
              onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
              data-testid="review-name"
              className="mt-4 w-full border border-[#E5DBD8] px-4 py-3 text-sm focus:outline-none focus:border-[#7A1F2B]"
            />
            <div className="flex items-center gap-2 mt-4">
              <span className="text-sm text-[#5C5555]">Rating:</span>
              {[1,2,3,4,5].map((s) => (
                <button type="button" key={s} onClick={() => setReviewForm({ ...reviewForm, rating: s })} data-testid={`review-star-${s}`}>
                  <Star className="w-5 h-5" strokeWidth={1.5} fill={s <= reviewForm.rating ? "#7A1F2B" : "none"} color="#7A1F2B"/>
                </button>
              ))}
            </div>
            <textarea
              placeholder="Your thoughts…"
              rows={4}
              value={reviewForm.comment}
              onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
              data-testid="review-comment"
              className="mt-4 w-full border border-[#E5DBD8] px-4 py-3 text-sm focus:outline-none focus:border-[#7A1F2B]"
            />
            <button type="submit" data-testid="review-submit" className="btn-primary mt-5 w-full">Submit Review</button>
          </form>
        </div>
      </section>

      {/* Recently viewed */}
      <RecentlyViewed excludeId={product.id} title="Recently viewed by you"/>

      {/* Related */}
      {related.length > 0 && (
        <section className="section-y">
          <div className="container-x">
            <p className="label-small">You may also love</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-10 text-[#2D2424]">Other gifts from this collection</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6" data-testid="related-products">
              {related.map((p) => <ProductCard key={p.id} product={p}/>)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
